import React, { useMemo } from 'react';
import { SelectPopover } from '@planne-software/uni';
import { Button } from '@planne-software/uni/Button';
import { Typography } from '@planne-software/uni/mui/material';
import { IconCheck } from '@tabler/icons-react';

import useBreakpoint from '@src/hooks/useBreakpoint';
import { useWeekdayFormatter } from '@src/domains/scheduling/hooks/useWeekdayFormatter';
import { POPOVER_WIDTH } from '@src/domains/scheduling/components/constants';
import MonthYearHeader from '@src/domains/scheduling/components/MonthYearHeader';
import CustomLegend from '@src/domains/scheduling/components/CustomLegend';
import MonthCard from '@src/domains/scheduling/components/MonthCard';
import { CalendarYearProvider } from '@src/domains/scheduling/components/CalendarYearContext';
import type { DateSelectorProps } from '@src/domains/scheduling/types/dateSelector';

import { ContentBox, FooterColumn, StyledDatePicker, TotalLabel, TotalRow, TotalValue } from './styles';

const DateSelector = (props: DateSelectorProps) => {
  const {
    placeholder,
    selectedDateFormatted,
    disabled,
    open,
    onOpenChange,
    pickerValue,
    referenceDate,
    onMonthChange,
    onConfirm,
    dayRenderer,
    legend,
    totalLabel,
    labels,
  } = props;

  const { isMobile } = useBreakpoint();
  const { format: formatWeekDay } = useWeekdayFormatter();

  const selectedElement = useMemo(() => {
    if (!selectedDateFormatted) {
      return undefined;
    }

    return (
      <Typography variant='text-sm-semibold' noWrap sx={{ textAlign: 'left', width: '100%' }}>
        {selectedDateFormatted}
      </Typography>
    );
  }, [selectedDateFormatted]);

  const confirmButton = useMemo(
    () => (
      <Button
        fullWidth
        color='primary'
        variant='contained'
        startSlot={<IconCheck size={20} />}
        disabled={!pickerValue}
        onClick={onConfirm}
      >
        {labels.confirm}
      </Button>
    ),
    [pickerValue, onConfirm, labels.confirm],
  );

  const mobileFooter = useMemo(
    () => (
      <FooterColumn>
        {totalLabel ? (
          <TotalRow>
            <TotalLabel>{labels.total}</TotalLabel>
            <TotalValue>{totalLabel}</TotalValue>
          </TotalRow>
        ) : null}
        {confirmButton}
      </FooterColumn>
    ),
    [totalLabel, labels.total, confirmButton],
  );

  return (
    <SelectPopover
      placeholder={placeholder}
      selectedElement={selectedElement}
      bottomSheetTitle={labels.bottomSheetTitle}
      isBottomSheet
      fullWidth
      disabled={disabled}
      open={open}
      onOpenChange={onOpenChange}
      popoverWidth={isMobile ? undefined : POPOVER_WIDTH}
      footer={isMobile ? mobileFooter : confirmButton}
    >
      <ContentBox isMobile={isMobile} aria-label='month-days'>
        <CustomLegend labels={legend.labels} showPriceDisclaimer={legend.showPriceDisclaimer} />
        <CalendarYearProvider value={referenceDate.getFullYear()}>
          <StyledDatePicker
            value={pickerValue}
            referenceDate={referenceDate}
            onMonthChange={onMonthChange}
            dayRenderer={dayRenderer}
            dayOfWeekFormatter={formatWeekDay}
            views={['year', 'month', 'day']}
            monthsPerRow={3}
            slots={{
              calendarHeader: MonthYearHeader,
              monthButton: MonthCard,
              actionBar: () => null,
              toolbar: () => null,
            }}
            slotProps={{
              // MUI's slotProps types don't model data-* attrs, so we cast. MonthCard reads
              // `data-month` to know which month it's rendering for the MonthThumb grid.
              monthButton: ((ownerState: { value?: number }) => ({
                'data-month': ownerState.value != null ? String(ownerState.value) : undefined,
              })) as never,
            }}
          />
        </CalendarYearProvider>
      </ContentBox>
    </SelectPopover>
  );
};

export default DateSelector;
